import { Image } from '@change/ds-core-ui/Image';
import { Text } from '@change/ds-core-ui/Text';
import { TikTokIcon } from '@change/ds-icons/TikTokIcon';
import { cn } from '@change/ds-utils';

import { SocialPostAvatar, SocialPostThumbnailFallback } from '#src/app/pages/socialStarter/shared/components';
import { useHydrationSafeImageFallback } from '#src/app/pages/socialStarter/shared/hooks';
import { normalizeHandle } from '#src/app/shared/utils/socialMedia';
import { truncate } from '#src/app/shared/utils/string';

const CAPTION_MAX_LENGTH = 120;

const PlatformBadge: React.FC<{ platform: string | null | undefined }> = ({ platform }) => {
	if (platform?.toLowerCase() !== 'tiktok') return null;
	return (
		<div
			className="border-stroke-weaker text-typography-strong bg-background-base absolute -right-1 -bottom-1 z-10 flex h-5 w-5 items-center justify-center rounded-full border shadow-sm"
			data-qa="social-post-preview-platform"
			aria-hidden="true"
		>
			<TikTokIcon size="x-small" />
		</div>
	);
};

const AuthorBlock: React.FC<AuthorBlockProps> = ({ name, handle, avatarUrl, platform }) => (
	<>
		<div className="relative shrink-0">
			<SocialPostAvatar name={name} handle={handle} avatarUrl={avatarUrl} size={40} qaId="social-post-preview-avatar" />
			<PlatformBadge platform={platform} />
		</div>
		<div className="flex min-w-0 flex-1 flex-col">
			{name && (
				<Text as="span" size="small" fontWeight="bold" className="truncate">
					{name}
				</Text>
			)}
			{handle && (
				<Text as="span" size="x-small" className="text-typography-weak truncate">
					@{handle}
				</Text>
			)}
		</div>
	</>
);

const Footer: React.FC<FooterProps> = ({ authorName, cleanedHandle, authorAvatarUrl, platform, caption }) => {
	const hasAuthor = Boolean(authorName || cleanedHandle);
	if (!hasAuthor && !caption) return null;

	return (
		<div
			className="bg-background-base border-stroke-weaker flex flex-col gap-3 border-t px-5 py-4"
			data-qa="social-post-preview-footer"
		>
			{hasAuthor && (
				<div className="flex items-center gap-3">
					<AuthorBlock name={authorName} handle={cleanedHandle} avatarUrl={authorAvatarUrl} platform={platform} />
				</div>
			)}
			{caption && (
				<Text as="p" size="small" className="text-typography-weak" data-qa="social-post-preview-caption">
					{caption}
				</Text>
			)}
		</div>
	);
};

const TikTokThumbnailMedia: React.FC<TikTokThumbnailMediaProps> = ({ thumbnailUrl, thumbnailAlt }) => {
	const { hasFailed, imageRef, showFallback } = useHydrationSafeImageFallback(thumbnailUrl);
	const thumbnailFallback = (
		<SocialPostThumbnailFallback variant="hero" qaId="social-post-preview-thumbnail-fallback" />
	);

	if (!thumbnailUrl || hasFailed) return thumbnailFallback;

	return (
		<Image
			ref={imageRef}
			src={thumbnailUrl}
			alt={thumbnailAlt}
			fallback={thumbnailFallback}
			onError={showFallback}
			className="absolute inset-0 h-full w-full object-cover"
			aria-hidden={thumbnailAlt === '' ? 'true' : undefined}
			data-qa="social-post-preview-thumbnail"
		/>
	);
};

const ThumbnailMedia: React.FC<ThumbnailMediaProps> = ({ thumbnailUrl, thumbnailAlt, platform }) => {
	if (platform?.toLowerCase() === 'tiktok') {
		return <TikTokThumbnailMedia thumbnailUrl={thumbnailUrl} thumbnailAlt={thumbnailAlt} />;
	}
	if (!thumbnailUrl) return null;

	return (
		<Image
			src={thumbnailUrl}
			alt={thumbnailAlt}
			className="absolute inset-0 h-full w-full object-cover"
			aria-hidden={thumbnailAlt === '' ? 'true' : undefined}
			data-qa="social-post-preview-thumbnail"
		/>
	);
};

export const SocialPostPreview: React.FC<SocialPostPreviewProps> = ({
	title,
	thumbnailUrl,
	thumbnailAlt = '',
	authorName,
	authorHandle,
	authorAvatarUrl,
	platform,
	caption,
	className,
}) => {
	const cleanedHandle = normalizeHandle(authorHandle);
	const truncatedCaption = caption ? truncate(caption, CAPTION_MAX_LENGTH) : null;

	return (
		<div
			className={cn(
				'elevation-elevated bg-background-base relative mx-auto w-full max-w-md overflow-hidden rounded-lg md:max-w-xl',
				className,
			)}
			data-qa="social-post-preview"
		>
			<div className="bg-background-raised relative aspect-[4/3] overflow-hidden">
				<ThumbnailMedia thumbnailUrl={thumbnailUrl} thumbnailAlt={thumbnailAlt} platform={platform} />
				<div className="absolute inset-x-0 bottom-0 h-1/2 bg-linear-to-t from-black/95 via-black/60 to-transparent" />

				<Text
					as="h2"
					variant="heading"
					size="small"
					className="text-typography-inverse-strong-fixed absolute right-5 bottom-5 left-5 text-balance"
				>
					{title}
				</Text>
			</div>

			<Footer
				authorName={authorName}
				cleanedHandle={cleanedHandle}
				authorAvatarUrl={authorAvatarUrl}
				platform={platform}
				caption={truncatedCaption}
			/>
		</div>
	);
};

type SocialPostPreviewProps = {
	title: string;
	thumbnailUrl?: string | null;
	thumbnailAlt?: string;
	authorName?: string | null;
	authorHandle?: string | null;
	authorAvatarUrl?: string | null;
	platform?: string | null;
	caption?: string | null;
	className?: string;
};

type FooterProps = {
	authorName: string | null | undefined;
	cleanedHandle: string | null;
	authorAvatarUrl: string | null | undefined;
	platform: string | null | undefined;
	caption: string | null;
};

type ThumbnailMediaProps = {
	thumbnailUrl: string | null | undefined;
	thumbnailAlt: string;
	platform: string | null | undefined;
};

type TikTokThumbnailMediaProps = Pick<ThumbnailMediaProps, 'thumbnailUrl' | 'thumbnailAlt'>;

type AuthorBlockProps = {
	name: string | null | undefined;
	handle: string | null;
	avatarUrl: string | null | undefined;
	platform: string | null | undefined;
};
